import type {ComponentSingleStyleConfig} from '@chakra-ui/react';
import {getToken} from '@chakra-ui/react';
import {defineStyle} from '@chakra-ui/styled-system';
import type {ThemingProps} from '@chakra-ui/system';
import {useStyleConfig} from '@chakra-ui/system';
import {mode} from '@chakra-ui/theme-tools';

import {shadows} from '../../../theme/shadows';

import {
  link,
  linkDanger,
  linkWarning,
  outline,
  primary,
  secondary,
  secondaryDanger,
  sizes,
  subtle,
  tertiary,
} from './tokens';

const variantPrimary = defineStyle(props => {
  const disabled = {
    bg: mode(primary.light[`background-disabled`], primary.dark[`background-disabled`])(props),
    color: mode(primary.light[`text-disabled`], primary.dark[`text-disabled`])(props),
  };

  return {
    bg: mode(primary.light[`background-default`], primary.dark[`background-default`])(props),
    color: primary.light[`text-default`],
    boxShadow: shadows.lg,

    _hover: {
      bg: mode(primary.light[`background-hover`], primary.dark[`background-hover`])(props),
      _disabled: disabled,
    },
    _active: {
      bg: mode(primary.light[`background-pressed`], primary.dark[`background-pressed`])(props),
    },
    _focus: {
      bg: mode(primary.light[`background-focus`], primary.dark[`background-focus`])(props),
    },
    _disabled: disabled,
  };
});

const variantSecondary = defineStyle(props => {
  const {colorScheme} = props;

  const map = {
    default: secondary,
    danger: secondaryDanger,
  };

  const {light, dark} = map?.[colorScheme as keyof typeof map] || map.default;

  const disabled = {
    bg: mode(light[`background-disabled`], dark[`background-disabled`])(props),
    color: mode(light[`text-disabled`], dark[`text-disabled`])(props),
  };

  return {
    bg: mode(light[`background-default`], dark[`background-default`])(props),
    color: mode(light[`text-default`], dark[`text-default`])(props),
    _hover: {
      bg: mode(light[`background-hover`], dark[`background-hover`])(props),
      _disabled: disabled,
    },
    _active: {
      bg: mode(light[`background-pressed`], dark[`background-pressed`])(props),
    },
    _focus: {
      bg: mode(light[`background-focus`], dark[`background-focus`])(props),
    },
    _disabled: disabled,
  };
});

const variantTertiary = defineStyle(props => {
  return {
    bg: `transparent`,
    color: mode(tertiary.light[`text-default`], tertiary.dark[`text-default`])(props),
    _hover: {
      bg: mode(tertiary.light[`background-hover`], tertiary.dark[`background-hover`])(props),
    },
    _active: {
      bg: mode(tertiary.light[`background-pressed`], tertiary.dark[`background-pressed`])(props),
    },
    _focus: {
      bg: mode(tertiary.light[`background-focus`], tertiary.dark[`background-focus`])(props),
    },
    _disabled: {
      bg: `transparent`,
      color: mode(tertiary.light[`text-disabled`], tertiary.dark[`text-disabled`])(props),
    },
  };
});

const variantLink = defineStyle(props => {
  const {colorScheme} = props;
  const map = {
    default: link,
    danger: linkDanger,
    warning: linkWarning,
  };

  const {light, dark} = map?.[colorScheme as keyof typeof map] || map.default;

  const disabled = {
    color: mode(light[`text-disabled`], dark[`text-disabled`])(props),
    _hover: {
      textDecoration: `none`,
    },
  };
  return {
    padding: 0,
    height: `auto`,
    lineHeight: `normal`,
    verticalAlign: `baseline`,
    color: mode(light[`text-default`], dark[`text-default`])(props),
    _disabled: disabled,
    _focus: {
      color: mode(light[`text-default`], dark[`text-default`])(props),
    },
    _hover: {
      color: mode(light[`text-hover`], dark[`text-hover`])(props),
      textDecoration: `underline`,
      _disabled: disabled,
    },
    _active: {
      color: mode(light[`text-pressed`], dark[`text-pressed`])(props),
    },
  };
});

const variantOutlined = defineStyle(props => {
  const getBoxShadow = (extra?: string) =>
    [extra, `0 1px 2px 0 rgba(0, 0, 0, 0.04)`, `0 1px 3px 0 rgba(0, 0, 0, 0.08)`].filter(Boolean).join(`, `);
  const disabled = {
    color: mode(outline.light[`text-disabled`], outline.dark[`text-disabled`])(props),
    bg: mode(outline.light[`background-disabled`], outline.dark[`background-disabled`])(props),
    borderColor: `transparent`,
    boxShadow: getBoxShadow(),
  };

  return {
    border: `1px solid`,
    borderColor: `transparent`,
    color: mode(outline.light[`text-default`], outline.dark[`text-default`])(props),
    bg: mode(outline.light[`background-default`], outline.dark[`background-default`])(props),
    boxShadow: getBoxShadow(),

    _hover: {
      color: mode(outline.light[`text-hover`], outline.dark[`text-hover`])(props),
      bg: mode(outline.light[`background-hover`], outline.dark[`background-hover`])(props),
      borderColor: `transparent`,
      _disabled: disabled,
    },
    _focusVisible: {
      color: mode(outline.light[`text-default`], outline.dark[`text-default`])(props),
      bg: mode(outline.light[`background-focus`], outline.dark[`background-focus`])(props),
      borderColor: `transparent`,
      boxShadow: getBoxShadow(
        `0 0 0 2px ${getToken(
          `colors`,
          mode(outline.light[`stroke-focus`], outline.dark[`stroke-focus`])(props),
        )(props.theme)}`,
      ),
      _disabled: disabled,
    },
    _active: {
      color: mode(outline.light[`text-pressed`], outline.dark[`text-pressed`])(props),
      bg: mode(outline.light[`background-pressed`], outline.dark[`background-pressed`])(props),
      borderColor: mode(outline.light[`stroke-hover`], outline.dark[`stroke-hover`])(props),
      _disabled: disabled,
    },
    _disabled: disabled,
  };
});

const variantSubtle = defineStyle(props => {
  const disabled = {
    bg: mode(subtle.light[`background-disabled`], subtle.dark[`background-disabled`])(props),
    color: mode(subtle.light[`text-disabled`], subtle.dark[`text-disabled`])(props),
  };
  const colorDefault = mode(subtle.light[`text-default`], subtle.dark[`text-default`])(props);

  return {
    color: colorDefault,
    bg: mode(subtle.light[`background-default`], subtle.dark[`background-default`])(props),
    _hover: {
      color: mode(subtle.light[`text-hover`], subtle.dark[`text-hover`])(props),
      bg: mode(subtle.light[`background-hover`], subtle.dark[`background-hover`])(props),
      _disabled: disabled,
    },
    _focusVisible: {
      borderColor: mode(subtle.light[`stroke-focus`], subtle.dark[`stroke-focus`])(props),
    },
    _focus: {
      color: colorDefault,
      borderColor: mode(subtle.light[`stroke-focus`], subtle.dark[`stroke-focus`])(props),
      bg: mode(subtle.light[`background-focus`], subtle.dark[`background-focus`])(props),
    },
    _active: {
      borderColor: `transparent`,
      color: mode(subtle.light[`text-pressed`], subtle.dark[`text-pressed`])(props),
      bg: mode(subtle.light[`background-pressed`], subtle.dark[`background-pressed`])(props),
    },
    _disabled: disabled,
  };
});

export const buttonTheme = {
  baseStyle: defineStyle(props => ({
    lineHeight: `1.2`,
    borderRadius: `radius-rounded`,
    fontWeight: `normal`,
    _focusVisible: {
      borderColor: mode(outline.light[`stroke-focus`], outline.dark[`stroke-focus`])(props),
      boxShadow: `none !important`,
    },
    _focus: {
      borderColor: mode(outline.light[`stroke-focus`], outline.dark[`stroke-focus`])(props),
    },
    _disabled: {
      opacity: 1,
      cursor: `not-allowed`,
      boxShadow: `none`,
    },
    _hover: {
      textDecoration: `none`,
      _disabled: {
        bg: `initial`,
      },
    },
  })),

  variants: {
    primary: variantPrimary,
    secondary: variantSecondary,
    tertiary: variantTertiary,
    outline: variantOutlined,
    link: variantLink,
    subtle: variantSubtle,
  },

  sizes,
  defaultProps: {
    variant: `primary`,
    size: `md`,
  },
} satisfies ComponentSingleStyleConfig;

export const useButtonStyleConfig = (props: ThemingProps) => {
  return useStyleConfig(`Button`, props ?? {});
};
